import {
  DURABLE_WORLD_PROTOCOL,
  DURABLE_WORLD_SOCKET_PATH,
  parseWorldStateServerMessage,
  type DurableWorldRecord,
  type WorldStateErrorResponse,
  type WorldStateInterest,
  type WorldStateMutationReceipt,
  type WorldStateSnapshot,
} from '../../shared/durableWorldState'
import {
  WORLD_PROTOCOL,
  WORLD_TICKET_SUBPROTOCOL_PREFIX,
} from '../../shared/worldProtocol'

interface SessionPayload {
  authenticated: true
  csrfToken: string
}

interface WorldTicketPayload {
  protocol: typeof WORLD_PROTOCOL
  ticket: string
  worldId: string
  expiresAt: string
  roomIds: string[]
}

export interface BrowserWorldStateClientOptions {
  webSocketFactory?: (url: string, protocols: string[]) => WebSocket
  fetchImpl?: typeof fetch
  onConnectionState?: (state: 'connecting' | 'connected' | 'degraded' | 'disconnected') => void
}

export interface WorldStateProjection {
  worldId: string
  epoch: string
  cursor: number
  records: DurableWorldRecord[]
}

export const WORLD_STATE_REFRESH_MS = 15_000

interface ResumeRequestState {
  selectorRevision: number
  continuation: boolean
  afterRecordId?: string
}

interface SnapshotAssembly {
  selectorRevision: number
  worldId: string
  epoch: string
  cursor: number
  baseInterestHash: string
}

/** Browser seam for the world service's Arena-owned durable projection. */
export class BrowserWorldStateClient {
  private socket: WebSocket | null = null
  private desired = false
  private generation = 0
  private reconnectAttempt = 0
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private refreshTimer: ReturnType<typeof setInterval> | null = null
  private worldId: string | null = null
  private epoch: string | undefined
  private cursor: number | undefined
  private interestHash: string | undefined
  private interest: WorldStateInterest = { roomIds: ['district'], limit: 500 }
  private selectorRevision = 0
  private readonly pendingResumes = new Map<string, ResumeRequestState>()
  private snapshotAssembly: SnapshotAssembly | null = null
  private deferredResume = false
  private readonly records = new Map<string, DurableWorldRecord>()
  private onProjection: (projection: WorldStateProjection) => void = () => {}
  private onReceipt: (receipt: WorldStateMutationReceipt) => void = () => {}
  private onError: (error: WorldStateErrorResponse) => void = () => {}

  constructor(private readonly options: BrowserWorldStateClientOptions = {}) {}

  connect(
    interest: WorldStateInterest,
    onProjection: (projection: WorldStateProjection) => void,
    onReceipt: (receipt: WorldStateMutationReceipt) => void = () => {},
    onError: (error: WorldStateErrorResponse) => void = () => {},
  ): void {
    this.interest = cloneInterest(interest)
    this.selectorRevision += 1
    this.snapshotAssembly = null
    this.onProjection = onProjection
    this.onReceipt = onReceipt
    this.onError = onError
    this.desired = true
    this.options.onConnectionState?.('connecting')
    const generation = ++this.generation
    void this.open(generation)
  }

  setInterest(interest: WorldStateInterest): void {
    this.interest = cloneInterest(interest)
    this.selectorRevision += 1
    this.snapshotAssembly = null
    // Echo the old hash once. The server detects the changed selector and
    // returns an authoritative interest_changed replacement snapshot.
    this.requestResume()
  }

  upsert(record: DurableWorldRecord, expectedRecordVersion?: number): string {
    const messageId = crypto.randomUUID()
    this.send({
      protocol: DURABLE_WORLD_PROTOCOL,
      type: 'state.upsert',
      messageId,
      record,
      ...(expectedRecordVersion === undefined ? {} : { expectedRecordVersion }),
    })
    return messageId
  }

  delete(worldId: string, recordId: string, expectedRecordVersion: number): string {
    const messageId = crypto.randomUUID()
    this.send({
      protocol: DURABLE_WORLD_PROTOCOL,
      type: 'state.delete',
      messageId,
      worldId,
      recordId,
      expectedRecordVersion,
    })
    return messageId
  }

  disconnect(): void {
    this.desired = false
    this.generation += 1
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer)
    this.reconnectTimer = null
    if (this.refreshTimer !== null) clearInterval(this.refreshTimer)
    this.refreshTimer = null
    this.socket?.close(1000, 'durable state closed')
    this.socket = null
    this.worldId = null
    this.epoch = undefined
    this.cursor = undefined
    this.interestHash = undefined
    this.pendingResumes.clear()
    this.snapshotAssembly = null
    this.deferredResume = false
    this.records.clear()
    this.options.onConnectionState?.('disconnected')
  }

  private async open(generation: number): Promise<void> {
    try {
      const fetchImpl = this.options.fetchImpl ?? fetch
      const sessionResponse = await fetchImpl('/api/v1/session', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      })
      if (!sessionResponse.ok) throw new Error(`world session failed (${sessionResponse.status})`)
      const session = await sessionResponse.json() as SessionPayload
      if (!this.desired || generation !== this.generation || !session.authenticated || !session.csrfToken) return
      const ticketResponse = await fetchImpl('/api/v1/world-ticket', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-Arena-CSRF': session.csrfToken },
      })
      if (!ticketResponse.ok) throw new Error(`world ticket failed (${ticketResponse.status})`)
      const ticket = await ticketResponse.json() as WorldTicketPayload
      if (!this.desired || generation !== this.generation) return
      if (
        ticket.protocol !== WORLD_PROTOCOL ||
        !ticket.ticket ||
        !ticket.worldId ||
        !validRoomIds(ticket.roomIds)
      ) throw new Error('world ticket invalid')
      if (!this.interest.roomIds || this.interest.roomIds.length === 0) {
        this.interest = { ...cloneInterest(this.interest), roomIds: [...ticket.roomIds] }
      }
      this.worldId = ticket.worldId
      const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const socket = (this.options.webSocketFactory ?? ((url, protocols) => new WebSocket(url, protocols)))(
        `${scheme}//${window.location.host}${DURABLE_WORLD_SOCKET_PATH}`,
        [DURABLE_WORLD_PROTOCOL, `${WORLD_TICKET_SUBPROTOCOL_PREFIX}${ticket.ticket}`],
      )
      this.socket = socket
      socket.addEventListener('open', () => {
        if (!this.desired || generation !== this.generation) return socket.close()
        this.reconnectAttempt = 0
        this.options.onConnectionState?.('connected')
        this.sendResume()
        if (this.refreshTimer !== null) clearInterval(this.refreshTimer)
        this.refreshTimer = setInterval(
          () => this.requestResume(),
          WORLD_STATE_REFRESH_MS,
        )
      })
      socket.addEventListener('message', (event) => this.onMessage(socket, event.data))
      socket.addEventListener('close', () => {
        if (this.socket === socket) this.socket = null
        this.pendingResumes.clear()
        this.snapshotAssembly = null
        if (this.refreshTimer !== null) clearInterval(this.refreshTimer)
        this.refreshTimer = null
        if (this.desired && generation === this.generation) {
          this.options.onConnectionState?.('degraded')
          this.scheduleReconnect()
        }
      })
    } catch {
      if (this.desired && generation === this.generation) {
        this.options.onConnectionState?.('degraded')
        this.scheduleReconnect()
      }
    }
  }

  private onMessage(socket: WebSocket, raw: unknown): void {
    if (typeof raw !== 'string') return socket.close(4400, 'invalid durable state message')
    let decoded: unknown
    try {
      decoded = JSON.parse(raw)
    } catch {
      return socket.close(4400, 'invalid durable state message')
    }
    const parsed = parseWorldStateServerMessage(decoded)
    if (!parsed.ok || !parsed.value) return socket.close(4400, 'invalid durable state message')
    const message = parsed.value
    if (message.type === 'state.changed') {
      if (message.worldId !== this.worldId) {
        socket.close(4400, 'invalid durable state world')
        return
      }
      this.requestResume()
      return
    }
    if (message.type === 'state.error') {
      if (message.requestMessageId) this.pendingResumes.delete(message.requestMessageId)
      this.onError(message)
      this.flushDeferredResume()
      return
    }
    if (message.type === 'state.receipt') {
      this.onReceipt(message)
      this.requestResume()
      return
    }
    const request = message.requestMessageId
      ? this.pendingResumes.get(message.requestMessageId)
      : undefined
    if (!request || !message.requestMessageId) {
      socket.close(4400, 'uncorrelated durable state message')
      return
    }
    this.pendingResumes.delete(message.requestMessageId)
    if (request.selectorRevision !== this.selectorRevision) return
    if (message.type === 'state.snapshot') {
      this.acceptSnapshotPage(socket, message, request)
      return
    }
    if (request.continuation || this.snapshotAssembly) {
      socket.close(4400, 'invalid durable state pagination')
      return
    }
    this.worldId = message.worldId
    this.epoch = message.epoch
    this.cursor = message.cursor
    this.interestHash = message.interestHash
    for (const delta of message.deltas) {
      if (delta.record) this.records.set(delta.record.id, delta.record)
      if (delta.tombstone) this.records.delete(delta.tombstone.recordId)
    }
    this.emitProjection()
    if (message.hasMore) this.sendResume()
    else this.flushDeferredResume()
  }

  private acceptSnapshotPage(
    socket: WebSocket,
    message: WorldStateSnapshot,
    request: ResumeRequestState,
  ): void {
    if (!this.snapshotAssembly) {
      if (request.continuation) {
        socket.close(4400, 'invalid durable state pagination')
        return
      }
      this.snapshotAssembly = {
        selectorRevision: request.selectorRevision,
        worldId: message.worldId,
        epoch: message.epoch,
        cursor: message.cursor,
        baseInterestHash: message.interestHash,
      }
      this.records.clear()
    } else if (
      !request.continuation ||
      this.snapshotAssembly.selectorRevision !== request.selectorRevision ||
      this.snapshotAssembly.worldId !== message.worldId ||
      this.snapshotAssembly.epoch !== message.epoch ||
      this.snapshotAssembly.cursor !== message.cursor
    ) {
      socket.close(4400, 'inconsistent durable state pagination')
      return
    }
    let previousRecordId = request.afterRecordId
    for (const record of message.records) {
      if (
        (previousRecordId !== undefined && record.id <= previousRecordId) ||
        this.records.has(record.id)
      ) {
        socket.close(4400, 'invalid durable state page ordering')
        return
      }
      this.records.set(record.id, record)
      previousRecordId = record.id
    }
    if (message.nextAfterRecordId) {
      if (
        message.records.length === 0 ||
        message.nextAfterRecordId !== message.records.at(-1)!.id
      ) {
        socket.close(4400, 'invalid durable state continuation')
        return
      }
      this.sendResume(message.nextAfterRecordId, true)
      return
    }
    const completed = this.snapshotAssembly
    this.snapshotAssembly = null
    this.worldId = completed.worldId
    this.epoch = completed.epoch
    this.cursor = completed.cursor
    this.interestHash = completed.baseInterestHash
    this.emitProjection()
    this.flushDeferredResume()
  }

  private requestResume(): void {
    if (this.pendingResumes.size > 0 || this.snapshotAssembly) {
      this.deferredResume = true
      return
    }
    this.sendResume()
  }

  private flushDeferredResume(): void {
    if (
      !this.deferredResume ||
      this.pendingResumes.size > 0 ||
      this.snapshotAssembly
    ) return
    this.deferredResume = false
    this.sendResume()
  }

  private sendResume(afterRecordId?: string, continuation = false): void {
    if (!this.worldId || this.socket?.readyState !== 1) return
    const messageId = crypto.randomUUID()
    const interest = {
      ...cloneInterest(this.interest),
      ...(afterRecordId === undefined ? {} : { afterRecordId }),
    }
    this.pendingResumes.set(messageId, {
      selectorRevision: this.selectorRevision,
      continuation,
      ...(interest.afterRecordId === undefined ? {} : { afterRecordId: interest.afterRecordId }),
    })
    this.socket.send(JSON.stringify({
      protocol: DURABLE_WORLD_PROTOCOL,
      type: 'state.resume',
      messageId,
      worldId: this.worldId,
      ...(
        continuation ||
        this.epoch === undefined ||
        this.cursor === undefined ||
        this.interestHash === undefined
        ? {}
        : { epoch: this.epoch, cursor: this.cursor, interestHash: this.interestHash }),
      interest,
    }))
  }

  private send(message: object): void {
    if (this.socket?.readyState === 1) this.socket.send(JSON.stringify(message))
  }

  private emitProjection(): void {
    if (!this.worldId || this.epoch === undefined || this.cursor === undefined) return
    this.onProjection({
      worldId: this.worldId,
      epoch: this.epoch,
      cursor: this.cursor,
      records: [...this.records.values()].sort((a, b) => a.id.localeCompare(b.id)),
    })
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null) return
    const delay = Math.min(10_000, 250 * 2 ** this.reconnectAttempt++)
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      if (this.desired) void this.open(this.generation)
    }, delay)
  }
}

function cloneInterest(interest: WorldStateInterest): WorldStateInterest {
  return {
    ...(interest.roomIds ? { roomIds: [...interest.roomIds] } : {}),
    ...(interest.zoneIds ? { zoneIds: [...interest.zoneIds] } : {}),
    ...(interest.recordKinds ? { recordKinds: [...interest.recordKinds] } : {}),
    ...(interest.afterRecordId ? { afterRecordId: interest.afterRecordId } : {}),
    limit: interest.limit,
  }
}

function validRoomIds(value: unknown): value is string[] {
  return Array.isArray(value) &&
    value.length > 0 &&
    value.length <= 128 &&
    new Set(value).size === value.length &&
    value.every((roomId) =>
      typeof roomId === 'string' &&
      /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/.test(roomId),
    )
}
